import { animate, style, transition, trigger } from '@angular/animations';
import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { API, DeviceConfigurationTokenDTO, DeviceFullDTO } from 'src/app/services/api/api';
import { ModalNavigationComponent } from 'src/app/services/gui/modal/modal-navigation-component';
import { ModalNavigationService } from 'src/app/services/gui/modal/modal-navigation.service';
import { SbPairingResultComponent } from '../sb-pairing-result/sb-pairing-result.component';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';

@Component({
  selector: 'sb-device-pairing',
  templateUrl: './sb-device-pairing.component.html',
  styleUrls: ['./sb-device-pairing.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate(250, style({ opacity: 1 }))]),
      transition(':leave', [animate(500, style({ opacity: 0 }))]),
    ]),
  ],
  standalone: false,
})
export class SbDevicePairingComponent implements OnInit, ModalNavigationComponent {
  isInModalContext: boolean;

  accept: boolean;
  mode: string;
  token: string;
  loading: boolean;

  @Input() device: DeviceFullDTO;

  constructor(
    private api: API,
    private modalNavigation: ModalNavigationService,
    private translate: TranslateService,
    private destroyRef: DestroyRef
  ) {
    this.accept = false;
    this.mode = '';
    this.token = '';
    this.loading = false;
  }

  ngOnInit() {
    this.accept = false;
    if (this.isInModalContext) {
      this.device = this.modalNavigation.getCurrentNavigation().data.device;
      this.mode = this.modalNavigation.getCurrentNavigation().data.mode;
    }
  }

  goBack() {
    this.modalNavigation.goBack();
  }

  onToken($event: string) {
    this.token = $event;
  }

  isSubmitValid() {
    switch (this.mode) {
      case 'PAIR':
        return this.accept && this.token;
      case 'UNPAIR':
        return this.accept;
    }
    return false;
  }

  pair() {
    this.loading = true;
    this.api
      .ss_nm_Device_pair(this.device.id, this.token)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (e: DeviceConfigurationTokenDTO) => {
          if (e) {
            this.pairSuccess(e);
          } else {
            this.pairFail();
          }
        },
        error: () => {
          this.pairFail();
        },
      });
  }

  unpair() {
    this.loading = true;
    this.api
      .ss_nm_Device_unpair(this.device.id)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (e: boolean) => {
          if (e) {
            this.unpairSuccess();
          } else {
            this.unpairFail();
          }
        },
        error: () => {
          this.unpairFail();
        },
      });
  }

  unpairFail() {
    this.modalNavigation.navigate(SbPairingResultComponent, {
      device: this.device,
      mode: 'PAIR',
      success: false,
      message: this.translate.instant('lang.device.unpairFail'), // 'Could not unpair the device!'
    });
  }

  unpairSuccess() {
    this.modalNavigation.clearAndNavigate(SbPairingResultComponent, {
      device: this.device,
      mode: 'UNPAIR',
      success: true,
      message: this.translate.instant('lang.device.unpairSuccessful'), // 'The device has been unpaired successfully!'
    });
  }

  pairFail() {
    this.modalNavigation.navigate(SbPairingResultComponent, {
      device: this.device,
      mode: 'PAIR',
      success: false,
      message: this.translate.instant('lang.device.pairFail'), // 'Could not pair the device! Please check your token!'
    });
  }

  pairSuccess(e: DeviceConfigurationTokenDTO) {
    this.modalNavigation.clearAndNavigate(SbPairingResultComponent, {
      device: this.device,
      mode: 'PAIR',
      success: true,
      message: this.translate.instant('lang.device.pairSuccessful'), // 'The device has been paired successfully!',
      configuration: e,
    });
  }
}
